// ═══════════════════════════════════════════════════════════════════════
// unifiedDFDTab.jsx — Unified Data Flow Diagram with REAL SVG canvas
// ═══════════════════════════════════════════════════════════════════════
// Three views:
//   1. SVG Canvas — proper DFD shapes (terminator/process/store) with
//                   animated dots flowing along edges, color per flow.
//   2. Stores list — drill into each data store
//   3. Narratives — story per tier-1 flow with clickable interfaces
//
// Cross-tab nav: clicks on interface refs route through useNavigation.
// Sources: CLM_Unified_DFD.pptx + agentic data + crossRefs
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { T, FONTS, chipBtn } from "./theme";
import { FLOWS, getInterfacesForFlow } from "./crossRefs";
import { EntityRefList } from "./navigation";

// ─── Geometry ────────────────────────────────────────────────────────
const W = 1180, H = 660;

// External entities (terminators) on the sides
const EXTERNAL = [
  { id: "ENG",   name: "Engineering",     icon: "👷", color: T.cyan, x: 30,    y: 60,  side: "L" },
  { id: "PLAN",  name: "Planners",        icon: "📋", color: T.amber, x: 30,    y: 160, side: "L" },
  { id: "OPER",  name: "Operators",       icon: "🛠️", color: T.orange, x: 30,    y: 260, side: "L" },
  { id: "QA",    name: "Quality team",    icon: "🔬", color: T.pink, x: 30,    y: 360, side: "L" },
  { id: "FIN",   name: "Finance",         icon: "💼", color: T.gold, x: 30,    y: 460, side: "L" },
  { id: "CUST",  name: "Customers",       icon: "🏢", color: T.green, x: 1010,  y: 100, side: "R" },
  { id: "SUPP",  name: "Suppliers",       icon: "🚚", color: T.amber, x: 1010,  y: 240, side: "R" },
  { id: "AUDIT", name: "Auditors",        icon: "⚖️", color: T.purple, x: 1010,  y: 380, side: "R" },
  { id: "MKT",   name: "Market signals",  icon: "📡", color: T.teal, x: 1010,  y: 510, side: "R" },
];

// Processes (rounded rectangles) in the middle
const PROCESSES = [
  { id: "P1", name: "PLM",   sub: "Teamcenter 2512",            x: 220, y: 60,  color: T.cyan },
  { id: "P2", name: "MES",   sub: "Opcenter EX 2507",          x: 450, y: 60,  color: T.orange },
  { id: "P3", name: "APS",   sub: "Opcenter APS 2510",         x: 680, y: 60,  color: T.amber },
  { id: "P4", name: "ERP",   sub: "SAP S/4HANA",                x: 220, y: 220, color: T.gold },
  { id: "P5", name: "SFC",   sub: "Edge · OPC-UA",              x: 450, y: 220, color: T.teal },
  { id: "P6", name: "Cloud", sub: "Insights Hub",               x: 680, y: 220, color: T.purple },
  { id: "P7", name: "Qual",  sub: "QM · SPC",                   x: 220, y: 380, color: T.pink },
  // central ESB hub
  { id: "P8", name: "ESB",   sub: "Kafka · Schema Registry",    x: 460, y: 380, color: T.green, big: true },
];

// Data stores (open rectangles) below the processes
const STORES = [
  { id: "D1", name: "Item / BOM",          x: 720, y: 380, color: T.cyan },
  { id: "D2", name: "ECO / ECN",           x: 30,  y: 570, color: T.cyan },
  { id: "D3", name: "MaterialMaster",      x: 180, y: 570, color: T.gold },
  { id: "D4", name: "WorkOrder",           x: 330, y: 570, color: T.orange },
  { id: "D5", name: "Prod Confirm",        x: 480, y: 570, color: T.green },
  { id: "D6", name: "NCR / Quality",       x: 630, y: 570, color: T.pink },
  { id: "D7", name: "Time-series",         x: 780, y: 570, color: T.teal },
  { id: "D8", name: "Audit Ledger",        x: 930, y: 570, color: T.purple },
];

// Edges — flow lines per tier-1 flow
const EDGES = [
  // F1 ECN propagation
  { from: "ENG", to: "P1", flow: "F1" },
  { from: "P1",  to: "D2", flow: "F1" },
  { from: "D2",  to: "P8", flow: "F1" },
  { from: "P8",  to: "P2", flow: "F1" },
  { from: "P8",  to: "P4", flow: "F1" },
  { from: "P8",  to: "P6", flow: "F1" },

  // F2 BOM publish
  { from: "P1", to: "D1", flow: "F2" },
  { from: "D1", to: "P8", flow: "F2" },
  { from: "P8", to: "P4", flow: "F2" },
  { from: "P8", to: "P2", flow: "F2" },

  // F3 WO release
  { from: "CUST", to: "P4", flow: "F3" },
  { from: "P4",   to: "D4", flow: "F3" },
  { from: "D4",   to: "P8", flow: "F3" },
  { from: "P8",   to: "P2", flow: "F3" },

  // F4 Prod confirm
  { from: "OPER", to: "P2", flow: "F4" },
  { from: "P2",   to: "P8", flow: "F4" },
  { from: "P8",   to: "D5", flow: "F4" },
  { from: "P8",   to: "P4", flow: "F4" },
  { from: "P8",   to: "P6", flow: "F4" },

  // F5 NCR + Containment
  { from: "QA", to: "P7",   flow: "F5" },
  { from: "P7", to: "P8",   flow: "F5" },
  { from: "P8", to: "P2",   flow: "F5" },
  { from: "P8", to: "P1",   flow: "F5" },
  { from: "P8", to: "D6",   flow: "F5" },
  { from: "P8", to: "AUDIT",flow: "F5" },

  // F6 OEE
  { from: "P5", to: "D7", flow: "F6" },
  { from: "D7", to: "P8", flow: "F6" },
  { from: "P8", to: "P6", flow: "F6" },
];

// ─── Narratives ──────────────────────────────────────────────────────
const TIER1_NARRATIVES = FLOWS.map(f => {
  const ifaces = getInterfacesForFlow(f.id);
  const map = {
    F1: { route: "ENG → P1 → D2 → P8 → P2 + P4 + P6",
          desc: "Engineer approves ECN in PLM. AIG raises event. ESB fans out to MES (effectivity), ERP (material master), Cloud (KPIs)." },
    F2: { route: "P1 → D1 → P8 → P4 + P2",
          desc: "Engineering commits new BOM revision. T4S syncs material master and STKO/STPO to SAP. Opcenter receives routing." },
    F3: { route: "CUST → P4 → D4 → P8 → P2",
          desc: "Customer order in SAP creates AUFNR. APS sequences. Released to Opcenter EX as work order with operations." },
    F4: { route: "OPER → P2 → P8 → D5 + P4 + P6",
          desc: "Operator confirms operation in Opcenter. ESB carries to SAP (AFRU/AFKO), Insights Hub (OEE), audit ledger." },
    F5: { route: "QA → P7 → P2 + P1 → D6 + P8",
          desc: "Quality raises NCR in MES. Containment scope determined. ESB carries to TC (change request), regulators (audit)." },
    F6: { route: "P5 → D7 → P8 → P6",
          desc: "OPC-UA tags from PLCs/CNC → Industrial Information Hub → ESB → Insights Hub time-series + dashboards." },
  };
  return { ...f, ...map[f.id], ifaces };
});

const VIEWS = [
  { id: "canvas",     label: "🎨 SVG Canvas",       color: T.cyan },
  { id: "stores",     label: "💾 Data Stores",      color: T.purple },
  { id: "narratives", label: "📖 Flow Narratives",  color: T.amber },
];

export default function UnifiedDFDTab() {
  const [view, setView]               = useState("canvas");
  const [activeFlow, setActiveFlow]   = useState("ALL");
  const [showAnimation, setShowAnimation] = useState(true);

  const nodeMap = useMemo(() => {
    const m = {};
    for (const n of EXTERNAL)  m[n.id] = { ...n, type: "ext" };
    for (const n of PROCESSES) m[n.id] = { ...n, type: "proc" };
    for (const n of STORES)    m[n.id] = { ...n, type: "store" };
    return m;
  }, []);

  const visibleEdges = useMemo(() => {
    if (activeFlow === "ALL") return EDGES;
    return EDGES.filter(e => e.flow === activeFlow);
  }, [activeFlow]);

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          🔄 CLM Unified Data Flow Diagram
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          {EXTERNAL.length} external entities · {PROCESSES.length} processes · {STORES.length} data stores · {FLOWS.length} tier-1 flow narratives
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={chipBtn(view === v.id, v.color)}>
            {v.label}
          </button>
        ))}
      </div>

      {view === "canvas" && (
        <div>
          <div style={{
            display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center",
            padding: "8px 12px", background: T.bg2, borderRadius: 6, border: `1px solid ${T.border}`,
          }}>
            <span style={{ fontSize: 9, color: T.text2, marginRight: 4, fontFamily: FONTS.mono, letterSpacing: 1 }}>
              FLOW ▸
            </span>
            <button onClick={() => setActiveFlow("ALL")} style={chipBtn(activeFlow === "ALL", T.cyan)}>
              All flows ({FLOWS.length})
            </button>
            {FLOWS.map(f => (
              <button key={f.id} onClick={() => setActiveFlow(f.id)} style={chipBtn(activeFlow === f.id, f.color)}>
                {f.id} · {f.name}
              </button>
            ))}
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ fontSize: 9, color: T.text2, display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={showAnimation} onChange={e => setShowAnimation(e.target.checked)} />
                Animate
              </label>
            </div>
          </div>

          <div style={{
            background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
            overflow: "auto", padding: 12,
          }}>
            <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
              <defs>
                {FLOWS.map(f => (
                  <marker key={f.id} id={`arr-${f.id}`}
                    viewBox="0 0 10 10" refX="9" refY="5"
                    markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M0,0 L10,5 L0,10 Z" fill={f.color}/>
                  </marker>
                ))}
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              {/* Edges */}
              {visibleEdges.map((e, i) => {
                const f = FLOWS.find(x => x.id === e.flow);
                const A = nodeMap[e.from], B = nodeMap[e.to];
                if (!A || !B || !f) return null;
                const path = pathBetween(A, B);
                const isActive = activeFlow === "ALL" || activeFlow === e.flow;
                return (
                  <g key={`e-${i}`}>
                    <path id={`p-${e.flow}-${i}`} d={path}
                      stroke={f.color}
                      strokeWidth={isActive ? 1.8 : 0.8}
                      strokeOpacity={isActive ? 0.85 : 0.18}
                      fill="none"
                      markerEnd={isActive ? `url(#arr-${e.flow})` : undefined}
                    />
                    {showAnimation && isActive && (
                      <circle r="3.5" fill={f.color} filter="url(#glow)">
                        <animateMotion
                          dur={`${2 + (i % 3) * 0.5}s`}
                          repeatCount="indefinite"
                          begin={`${(i % 4) * 0.4}s`}>
                          <mpath href={`#p-${e.flow}-${i}`}/>
                        </animateMotion>
                      </circle>
                    )}
                  </g>
                );
              })}

              {/* External entities */}
              {EXTERNAL.map(n => (
                <g key={n.id} transform={`translate(${n.x}, ${n.y})`}>
                  <rect width="160" height="58" rx="3" fill={T.bg3} stroke={n.color} strokeWidth="2"/>
                  <text x="14" y="24" fontSize="20" fill={n.color}>{n.icon}</text>
                  <text x="46" y="22" fontSize="10.5" fill={T.text} fontWeight="700">{n.name}</text>
                  <text x="46" y="40" fontSize="8" fill={T.text2} fontFamily={FONTS.mono}>{n.id}</text>
                  <text x="14" y="52" fontSize="7" fill={T.text3} fontFamily={FONTS.mono} letterSpacing="1">EXTERNAL</text>
                </g>
              ))}

              {/* Processes */}
              {PROCESSES.map(n => {
                const w = n.big ? 220 : 180, h = 90;
                return (
                  <g key={n.id} transform={`translate(${n.x}, ${n.y})`}>
                    <rect width={w} height={h} rx="20" ry="20" fill={`${n.color}10`} stroke={n.color} strokeWidth="2"/>
                    <text x="14" y="24" fontSize="10" fontFamily={FONTS.mono} fill={n.color} fontWeight="700">{n.id}</text>
                    <text x={w / 2} y="48" fontSize="16" fontFamily={FONTS.header} fill={T.text} textAnchor="middle" fontWeight="700">{n.name}</text>
                    <text x={w / 2} y="66" fontSize="9" fill={T.text2} textAnchor="middle" fontStyle="italic">{n.sub}</text>
                    {n.big && (
                      <text x={w / 2} y="82" fontSize="7" fill={T.green} fontFamily={FONTS.mono} letterSpacing="1" textAnchor="middle">HUB · ALL TIER-1 FLOWS</text>
                    )}
                  </g>
                );
              })}

              {/* Data stores */}
              {STORES.map(n => (
                <g key={n.id} transform={`translate(${n.x}, ${n.y})`}>
                  <line x1="0" y1="0"  x2="140" y2="0"  stroke={n.color} strokeWidth="2.5"/>
                  <line x1="0" y1="44" x2="140" y2="44" stroke={n.color} strokeWidth="2.5"/>
                  <text x="6" y="18" fontSize="10" fontFamily={FONTS.mono} fill={n.color} fontWeight="700">{n.id}</text>
                  <text x="6" y="34" fontSize="9.5" fill={T.text}>{n.name}</text>
                </g>
              ))}

              {/* Legend */}
              <g transform={`translate(${W - 270}, ${H - 110})`}>
                <rect width="240" height="90" rx="4" fill={T.bg3} stroke={T.border} strokeWidth="1"/>
                <text x="12" y="18" fontSize="9" fill={T.text2} fontFamily={FONTS.mono} letterSpacing="1">DFD SHAPES</text>
                <rect x="12" y="26" width="24" height="11" rx="1" fill="none" stroke={T.cyan} strokeWidth="1.5"/>
                <text x="44" y="35" fontSize="8.5" fill={T.text}>Terminator (external)</text>
                <rect x="12" y="44" width="24" height="11" rx="5" ry="5" fill="none" stroke={T.green} strokeWidth="1.5"/>
                <text x="44" y="53" fontSize="8.5" fill={T.text}>Process</text>
                <line x1="12" y1="64" x2="36" y2="64" stroke={T.purple} strokeWidth="1.5"/>
                <line x1="12" y1="72" x2="36" y2="72" stroke={T.purple} strokeWidth="1.5"/>
                <text x="44" y="71" fontSize="8.5" fill={T.text}>Data store</text>
              </g>
            </svg>
          </div>

          <div style={{
            marginTop: 10, padding: "8px 14px",
            background: T.bg2, border: `1px solid ${T.cyan}30`, borderRadius: 6,
            fontSize: 9, color: T.text2, fontStyle: "italic",
          }}>
            💡 Pick a flow above to isolate it · animated dots show real-time message movement · the ESB hub at P8 routes every tier-1 flow.
          </div>
        </div>
      )}

      {view === "stores" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
          {STORES.map(s => (
            <div key={s.id} style={{
              background: T.bg2,
              borderTop: `3px solid ${s.color}`, borderBottom: `3px solid ${s.color}`,
              borderLeft: `1px solid ${s.color}30`, borderRight: `1px solid ${s.color}30`,
              padding: "12px 14px",
            }}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 14, fontWeight: 700, color: s.color, marginBottom: 4 }}>
                {s.id} · {s.name}
              </div>
              <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.5 }}>
                Data store · accessed by multiple processes · governed by schema registry · part of audit ledger.
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "narratives" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
          {TIER1_NARRATIVES.map(n => (
            <div key={n.id} style={{
              background: T.bg2, border: `1px solid ${n.color}40`,
              borderLeft: `4px solid ${n.color}`, borderRadius: 6, padding: "14px 16px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <span style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: n.color, color: "#000",
                  fontFamily: FONTS.header, fontSize: 13, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{n.id}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700, color: T.text }}>{n.name}</div>
                  <div style={{ fontSize: 9, color: n.color, fontFamily: FONTS.mono, marginTop: 2 }}>{n.route}</div>
                </div>
                <span style={{
                  fontSize: 9, color: T.green, background: `${T.green}15`,
                  padding: "3px 10px", borderRadius: 12, fontWeight: 600, border: `1px solid ${T.green}40`,
                }}>{n.sla}</span>
              </div>
              <div style={{ fontSize: 10.5, color: T.text2, lineHeight: 1.6, paddingLeft: 48, marginBottom: 8 }}>
                {n.desc}
              </div>
              {n.ifaces.length > 0 && (
                <div style={{ paddingLeft: 48 }}>
                  <EntityRefList kind="interface" ids={n.ifaces} label="Interfaces involved" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Cubic Bezier path between two nodes
function pathBetween(A, B) {
  const aw = A.type === "proc" ? (A.big ? 220 : 180) : (A.type === "ext" ? 160 : 140);
  const ah = A.type === "proc" ? 90 : (A.type === "ext" ? 58 : 44);
  const bw = B.type === "proc" ? (B.big ? 220 : 180) : (B.type === "ext" ? 160 : 140);
  const bh = B.type === "proc" ? 90 : (B.type === "ext" ? 58 : 44);

  const aCx = A.x + aw / 2, aCy = A.y + ah / 2;
  const bCx = B.x + bw / 2, bCy = B.y + bh / 2;
  const dx = bCx - aCx, dy = bCy - aCy;

  let ax, ay, bx, by;
  if (Math.abs(dx) > Math.abs(dy)) {
    ax = dx > 0 ? A.x + aw : A.x;     ay = aCy;
    bx = dx > 0 ? B.x      : B.x + bw; by = bCy;
  } else {
    ax = aCx; ay = dy > 0 ? A.y + ah : A.y;
    bx = bCx; by = dy > 0 ? B.y       : B.y + bh;
  }

  const mx = (ax + bx) / 2;
  return `M ${ax} ${ay} C ${mx} ${ay}, ${mx} ${by}, ${bx} ${by}`;
}
